import { useEffect, useRef } from "react";
import {
  isFroalaEditorTarget,
  syncInlineToolbarVisibility,
} from "../../Uitls/froalaSelectionUtils";

const applyScrollbarStyles = (component) => {
  if (component?.componentStyles?.toggleScroll !== true) return;

  const parentScrollBar = document.getElementById(component.id);
  if (!parentScrollBar) return;

  const thumbElements = parentScrollBar.getElementsByClassName("rc-scrollbars-thumb");
  const trackElements = parentScrollBar.getElementsByClassName("rc-scrollbars-track-v");
  const thumbVerticalElements =
    parentScrollBar.getElementsByClassName("rc-scrollbars-thumb-v");

  if (thumbElements[1]) {
    thumbElements[1].style.backgroundImage = `url("${component.componentStyles.scrollThumbBg} ")`;
    thumbElements[1].style.width = component.componentStyles.thumbWidth + "px";
  }

  if (trackElements[0]) {
    trackElements[0].style.borderRadius = component.componentStyles.trackRadius + "px";
    trackElements[0].style.backgroundColor = component.componentStyles.thumbColor;
    trackElements[0].style.width = component.componentStyles.trackWidth + "px";
  }

  if (thumbVerticalElements[0]) {
    thumbVerticalElements[0].style.borderRadius =
      component.componentStyles.trackRadius + "px";
  }

  if (component.componentStyles.trackRight && thumbVerticalElements[0] && trackElements[0]) {
    thumbVerticalElements[0].style.right = component.componentStyles.thumbRight + "px";
    trackElements[0].style.right = component.componentStyles.trackRight + "px";
  }
};

export const useEditorCanvasDomEffects = ({
  setVisibility,
  setShowPaste,
  layerCompData,
  createFroalaInstance,
  isEditorOn,
  setIsEditorOn,
}) => {
  const froalaSelectDragRef = useRef({
    moved: false,
    insideEditor: false,
    startX: 0,
    startY: 0,
  });

  useEffect(() => {
    const onMouseDown = (e) => {
      if (isFroalaEditorTarget(e.target)) {
        froalaSelectDragRef.current = {
          moved: false,
          insideEditor: true,
          startX: e.clientX,
          startY: e.clientY,
        };
      } else {
        froalaSelectDragRef.current = {
          moved: false,
          insideEditor: false,
          startX: 0,
          startY: 0,
        };
      }
    };

    const onMouseMove = (e) => {
      const dragState = froalaSelectDragRef.current;
      if (!dragState.insideEditor) return;

      const dx = Math.abs(e.clientX - dragState.startX);
      const dy = Math.abs(e.clientY - dragState.startY);
      if (dx > 2 || dy > 2) {
        dragState.moved = true;
      }
    };

    document.addEventListener("mousedown", onMouseDown, true);
    document.addEventListener("mousemove", onMouseMove, true);

    return () => {
      document.removeEventListener("mousedown", onMouseDown, true);
      document.removeEventListener("mousemove", onMouseMove, true);
    };
  }, []);

  useEffect(() => {
    const canvasCol = document.querySelector(".canvasCol");
    if (!canvasCol) return;

    const handleCanvasScroll = () => {
      setVisibility(false);
      setShowPaste(false);
    };

    canvasCol.addEventListener("scroll", handleCanvasScroll);
    return () => {
      canvasCol.removeEventListener("scroll", handleCanvasScroll);
    };
  }, [setVisibility, setShowPaste]);

  useEffect(() => {
    const canvasContainer = document.getElementById("mainCanvasContainer");
    if (canvasContainer) {
      canvasContainer.onclick = function () {
        setShowPaste(false);
      };
    }

    layerCompData.forEach((component) => {
      applyScrollbarStyles(component);
    });

    return () => {
      if (canvasContainer) {
        canvasContainer.onclick = null;
      }
    };
  }, [layerCompData, setShowPaste]);

  const clearNativeTextSelection = () => {
    if (typeof window === "undefined" || !window.getSelection) return;
    const currentSelection = window.getSelection();
    if (currentSelection && currentSelection.rangeCount > 0) {
      currentSelection.removeAllRanges();
    }
  };

  const clearFroalaTextSelection = (blurEditor = false) => {
    const refFroala = createFroalaInstance.current || [];
    if (refFroala.length > 0) {
      for (let i = 0; i < refFroala.length; i++) {
        const editorRef = refFroala[i];
        if (
          editorRef &&
          editorRef.editorInitialized &&
          typeof editorRef.getEditor === "function"
        ) {
          const editorInstance = editorRef.getEditor();
          if (editorInstance?.selection) {
            editorInstance.selection.clear();
          }
          if (blurEditor) {
            if (editorInstance?.events?.blur) {
              editorInstance.events.blur();
            }
            if (editorRef?.editor?.el?.blur) {
              editorRef.editor.el.blur();
            }
          }
        }
      }
    }
    clearNativeTextSelection();
  };

  const froalaEditorHandleOnclciksAndDrag = (e, index) => {
    const wasTextDragSelect =
      froalaSelectDragRef.current.moved &&
      froalaSelectDragRef.current.insideEditor;
    froalaSelectDragRef.current = {
      moved: false,
      insideEditor: false,
      startX: 0,
      startY: 0,
    };

    const clickedInsideEditor = isFroalaEditorTarget(e.target);
    const refFroala = createFroalaInstance.current;
    if (refFroala.length > 0) {
      for (let x in refFroala) {
        if (refFroala[x] && refFroala[x].editorInitialized) {
          const editor = refFroala[x].editor;
          const isActiveEditor = Number(x) === index;

          if (e.detail !== 2 && isEditorOn !== "dragged") {
            if (clickedInsideEditor && isActiveEditor && isEditorOn === "true") {
              editor.el.focus();

              if (
                isEditorOn !== "notClickedYet" &&
                editor.selection.text() !== "" &&
                !wasTextDragSelect
              ) {
                editor.selection.clear();
                editor.markers.insertAtPoint(e);
              } else if (wasTextDragSelect) {
                syncInlineToolbarVisibility(editor);
              }
            } else if (clickedInsideEditor && isActiveEditor) {
              editor.el.blur();
            } else if (isEditorOn === "notClickedYet") {
              editor.el.blur();
              setIsEditorOn("false");
            } else if (
              isEditorOn !== "notClickedYet" &&
              isEditorOn !== "dragged" &&
              editor.selection.text() !== "" &&
              !wasTextDragSelect
            ) {
              editor.selection.clear();
              editor.markers.insertAtPoint(e);
            }
          } else if (e.detail === 2 || isEditorOn === "dragged") {
            if (isActiveEditor) {
              editor.el.focus();
              setIsEditorOn("true");
              syncInlineToolbarVisibility(editor);
            }
          }
        }
      }
    }
  };

  return {
    froalaEditorHandleOnclciksAndDrag,
    clearFroalaTextSelection,
  };
};
